<!--
  Modal - Standard modal dialog with consistent styling

  Based on HeadlessUI Dialog with standardized sizing and transitions.

  Usage:
    [Modal :open="isOpen" title="Edit Item" size="large" @close="isOpen = false"]
      [template #default]
        [p]Modal content goes here[/p]
      [/template]
      [template #footer]
        [Button variant="cancel" @click="isOpen = false"]Cancel[/Button]
        [Button variant="primary" @click="handleSave"]Save[/Button]
      [/template]
    [/Modal]

    For raw content without header/padding:
    [Modal :open="isOpen" raw @close="isOpen = false"]
      [MyCustomComponent v-bind="props" /]
    [/Modal]

  Sizes:
    - small: max-w-sm (384px)
    - medium: max-w-md (448px) - default
    - large: max-w-2xl (672px)
    - xl: max-w-5xl (1024px)
    - full: max-w-7xl (1280px)
-->
<template>
    <TransitionRoot as="template" :show="open">
        <Dialog class="relative z-[1100]" @close="handleClose">
            <!-- Backdrop -->
            <TransitionChild
                as="template"
                enter="ease-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in duration-200"
                leave-from="opacity-100"
                leave-to="opacity-0"
            >
                <div
                    class="fixed inset-0 bg-black/25 dark:bg-black/50 transition-opacity"
                    aria-hidden="true"
                />
            </TransitionChild>

            <!-- Modal container -->
            <div class="fixed inset-0 overflow-y-auto">
                <div class="flex min-h-full items-start justify-center p-4 text-center pt-16 pb-16 sm:items-center sm:pt-0">
                    <TransitionChild
                        as="template"
                        enter="ease-out duration-300"
                        enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enter-to="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leave-from="opacity-100 translate-y-0 sm:scale-100"
                        leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <DialogPanel
                            :class="[
                                'w-full transform overflow-hidden rounded-2xl bg-white dark:bg-gray-900 text-left align-middle shadow-xl transition-all',
                                sizeClass,
                                raw ? '' : 'p-6',
                                'max-h-[85vh] overflow-y-auto'
                            ]"
                        >
                            <!-- Header (when not raw) -->
                            <div v-if="!raw && (title || $slots.header)" class="flex items-center justify-between mb-4">
                                <slot name="header">
                                    <DialogTitle
                                        as="h3"
                                        class="text-lg font-semibold leading-6 text-gray-900 dark:text-white"
                                    >
                                        {{ title }}
                                    </DialogTitle>
                                </slot>
                                <button
                                    v-if="showCloseButton"
                                    type="button"
                                    class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
                                    @click="handleClose"
                                >
                                    <span class="sr-only">Close</span>
                                    <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                                </button>
                            </div>

                            <!-- Content -->
                            <div :class="raw ? '' : 'mt-2'">
                                <slot />
                            </div>

                            <!-- Footer slot -->
                            <div v-if="$slots.footer" class="mt-6 flex justify-end gap-3">
                                <slot name="footer" />
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot
} from '@headlessui/vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

type ModalSize = 'small' | 'medium' | 'large' | 'xl' | 'full'

interface Props {
    /** Whether the modal is open */
    open: boolean
    /** Modal title (displayed in header) */
    title?: string
    /** Modal size */
    size?: ModalSize
    /** Raw mode - no padding or header, just renders slot content */
    raw?: boolean
    /** Show close button in header */
    showCloseButton?: boolean
    /** Prevent closing on backdrop click or escape key */
    persistent?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    size: 'medium',
    raw: false,
    showCloseButton: true,
    persistent: false
})

const emit = defineEmits<{
    close: []
}>()

const sizeClasses: Record<ModalSize, string> = {
    small: 'max-w-sm',
    medium: 'max-w-md',
    large: 'max-w-2xl',
    xl: 'max-w-5xl',
    full: 'max-w-7xl'
}

const sizeClass = computed(() => sizeClasses[props.size])

const handleClose = () => {
    if (!props.persistent) {
        emit('close')
    }
}
</script>
